import { Pipe, PipeTransform, inject } from '@angular/core';
import { TerminologyService } from '../../core/services/terminology/terminology';
import {
  TerminologyKey,
  TerminologyParams,
  TerminologyResolveOptions,
} from '../../core/models/terminology.interface';

/**
 * Translates a terminology key against the active sector catalog.
 *
 * `pure: false` is required because the first argument (the key) stays
 * stable when the user switches sector; the pipe must re-query the service.
 * Reading `terms()` / `revision()` keeps the pipe signal-aware for zoneless CD.
 *
 * @example
 * {{ 'client.singular' | term }}
 * {{ 'client.singular' | term:'Usuario' }}
 * {{ 'welcome' | term:{ name: profile.first_name } }}
 */
@Pipe({
  name: 'term',
  standalone: true,
  pure: false,
})
export class TermPipe implements PipeTransform {
  private readonly terminology = inject(TerminologyService);

  transform(key: TerminologyKey | null | undefined): string;
  transform(key: TerminologyKey | null | undefined, fallback: string): string;
  transform(key: TerminologyKey | null | undefined, params: TerminologyParams): string;
  transform(
    key: TerminologyKey | null | undefined,
    options: TerminologyResolveOptions,
  ): string;
  transform(
    key: TerminologyKey | null | undefined,
    fallback: string,
    params: TerminologyParams,
  ): string;
  transform(
    key: TerminologyKey | null | undefined,
    fallbackOrParams?: string | TerminologyParams | TerminologyResolveOptions,
    params?: TerminologyParams,
  ): string {
    this.terminology.terms();
    this.terminology.revision();

    if (this.isResolveOptions(fallbackOrParams)) {
      return this.terminology.resolve(key, fallbackOrParams);
    }

    if (this.isParams(fallbackOrParams)) {
      return this.terminology.resolve(key, { params: fallbackOrParams });
    }

    return this.terminology.resolve(key, fallbackOrParams, params);
  }

  private isParams(
    value: string | TerminologyParams | TerminologyResolveOptions | undefined,
  ): value is TerminologyParams {
    return typeof value === 'object' && value !== null && !('fallback' in value || 'params' in value);
  }

  private isResolveOptions(
    value: string | TerminologyParams | TerminologyResolveOptions | undefined,
  ): value is TerminologyResolveOptions {
    return typeof value === 'object' && value !== null && ('fallback' in value || 'params' in value);
  }
}
